import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {ConfirmationService} from 'primeng/api';
import {Observable} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {CwRequestDto} from '../../../model/dto/entity/cw-request.dto';
import {CwRequestService} from '../../../service/api/cw-request.service';

/**
 * Object component of {@code cw.CwRequest} (Antragseingang / Beraterantrag).
 *
 * <h3>What this component is for</h3>
 * Until it existed, {@code cw.CwRequest} was registered with a route and a service but WITHOUT an
 * {@code objectComponent}, so no {@code mvs-object} host and no widget could render an
 * Antragseingang at all — the alias resolved to nothing and the surface stayed empty. This is the
 * shell that closes that gap.
 *
 * <h3>Shape</h3>
 * It is a thin {@code ObjectBaseComponent}: the base class loads the row, this class hands it to
 * exactly one of two presentations, chosen by {@code uiMode} (same branch as
 * {@code CwCaseObjectComponent}):
 * <ul>
 *   <li>{@code 'inline'} → the compact card ({@code cw-request-inline-component}), which loads on
 *       its own because it is also mounted from lists that hold only identifiers.</li>
 *   <li>everything else → the context header, which is INPUT-DRIVEN: the DTO this shell already
 *       loaded is pushed into it, so the row is fetched once and there is one refresh path.</li>
 * </ul>
 *
 * The capture surface deliberately stays with {@code CwRequestCapturePage}: the entity-provider
 * registers {@code objectComponent} only and NOT {@code objectPageComponent}, so the route
 * {@code cw/request/:id} keeps resolving to that page.
 *
 * <h3>No lifecycle overrides here — on purpose</h3>
 * {@code ngOnInit} / {@code ngOnChanges} / {@code ngOnDestroy} are NOT redeclared. Angular resolves
 * a directive's hooks off {@code type.prototype}, which walks the prototype chain, and the emitted
 * {@code ɵɵInheritDefinitionFeature} additionally carries the base definition's hooks — including
 * {@code ɵɵNgOnChangesFeature}, which is marked {@code ngInherit} — onto this subclass. The hooks
 * of {@code ObjectBaseComponent} therefore run unchanged, and the module has live proof of it:
 * plenty of shipped object components (bm-creditor, bm-billing-area, ui-component, …) never
 * declare {@code ngOnInit} yet load their row, which only the inherited hook can do. Pass-through
 * overrides that add nothing but a {@code super} call would only invite the next reader to put
 * logic in front of the {@code super} call and break the load order. Re-add one ONLY with real
 * work in it, and then keep the {@code super} call FIRST.
 */
@Component({
    selector: 'cw-request-object-component',
    templateUrl: './cw-request-object.component.html',
    styleUrls: ['./cw-request-object.component.scss'],
    standalone: false
})
export class CwRequestObjectComponent extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    /** Typed view of {@link dto} — the base class field is untyped by contract. */
    request: CwRequestDto;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService) {
        super(coreService, messageService, confirmationService, observerService);
    }

    /**
     * Reads the intake WITH resolved FK/enum text.
     *
     * The base class default ({@code crudService.get(id)}) omits the resolve flags, and without
     * them {@code requestTypeDtoName} / {@code customerDtoName} arrive empty — the header would
     * show a Beraterantrag without an Antragstyp. Overriding this hook is the intended way to say
     * so ({@code ObjectBaseComponent#getRetrieveObjectObservable} is {@code protected} for exactly
     * this).
     *
     * It DELEGATES to {@code CwRequestService.getRequest} instead of repeating that call's resolve
     * flags. Those flags are POSITIONAL parameters of {@code get()} (see the service's own warning
     * on {@code getRequest}), so a second spelled-out copy here would be a silent fork: change the
     * resolution depth in the service and this component keeps reading at the old depth without
     * anything failing. One read definition, one place. The cast is needed only because the base
     * class types {@code crudService} as the generic {@code MvsCrudService}; the registry resolves
     * {@code cw.CwRequest} to {@code CwRequestService}, so it is always the real type.
     */
    protected override getRetrieveObjectObservable(): Observable<any> {
        return (<CwRequestService>this.crudService).getRequest(this.objectIdentifier.objectId);
    }

    /**
     * Called by the base class once the DTO is in — never from {@code ngOnInit}. It only publishes
     * the typed view and the page title; it must NEVER call {@code refreshObject()}, which would
     * loop (the load calls this method).
     */
    override onObjectChanged(): void {
        this.request = <CwRequestDto>this.dto;
        this.setPageTitle(`Beraterantrag #${this.request?.id ?? ''}`);
    }

    /**
     * The header cancelled the intake. It does not own the DTO, so the reload happens here — this
     * is an EVENT handler, not {@code onObjectChanged()}, so refreshing is safe.
     */
    onRequestCancelled(): void {
        this.refreshObject(false);
    }

}
